import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateSubtramiteEstado } from '@/services/tramiteService';
import { checkAnyPermissionsFromSubtramite, PERMISSIONS } from '@/auth/permissions';
import { useAuth } from '@/auth';
import { useAlert } from './useAlert';

interface SubtramiteData {
  tramite_id: number;
  habilitado: boolean;
  fecini: Date;
  files?: any[];
  fieldsDelete?: any[];
  filesDelete?: any[];
  [key: string]: any;
}

interface UseSubtramiteFormOptions {
  subtramiteKey: string;
  defaultData: Partial<SubtramiteData>;
  saveFunction: (data: { data: SubtramiteData }) => Promise<any>;
  successMessage: string;
  errorTitle: string;
}

const mapResult = (result: any, categoria: any, key: string) => {
  if (key === 'otros') {
    return {
      ...result,
      otros: (result.otros ?? []).find((o: any) => o.categoria === categoria)
    }
  }
  return result;
}


export const useSubtramiteForm = ({
  subtramiteKey,
  defaultData,
  saveFunction,
  successMessage,
  errorTitle,
}: UseSubtramiteFormOptions) => {
  const { state, pathname } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { errorAlert, successAlert } = useAlert();

  if (!state?.tramite) {
    throw new Error('Tramite data is required');
  }

  const tramite = state.tramite;
  const subtramiteData = tramite[subtramiteKey];

  const [data, setData] = useState<SubtramiteData>({
    tramite_id: tramite.id,
    habilitado: true,
    fecini: new Date(),
    ...defaultData,
    ...subtramiteData,
  });

  const [loading, setLoading] = useState(false);
  const init = !!subtramiteData?.tramite_id;
  const [isUpdate, setIsUpdate] = useState(() => init);

  const handleChange = ({ name, value }: { name: string; value: any }) => {
    setData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await saveFunction({ data });

      await navigate(pathname, {
        state: {
          ...state,
          tramite: mapResult(result, state.categoria, subtramiteKey),
        },
        replace: true,
        flushSync: true,
      });
      successAlert({ message: successMessage });
    } catch (err) {
      errorAlert({ title: errorTitle, exception: err });
    } finally {
      setLoading(false);
    }
  };

  const btnGuardarVisible = useMemo(
    () => checkAnyPermissionsFromSubtramite(
      user,
      PERMISSIONS.UPLOAD_FILES.name,
      tramite,
      subtramiteKey,
      false
    ),
    [data.files]
  );

  const handleFinalizarSubtramite = async () => {
    setLoading(true);
    try {
      const tablesMap = {
        reserva: 'reservaprioridad',
        presupuesto: 'presupuestos',
      }
      const result = await updateSubtramiteEstado({
        tipo: tablesMap[subtramiteKey] || subtramiteKey,
        id: data.id,
        estado: 'finalizado',
      })

      await navigate(pathname, {
        state: {
          ...state,
          tramite: mapResult(result, state.categoria, subtramiteKey),
        },
        replace: true,
        flushSync: true,
      });

      successAlert({ message: successMessage });
    }
    catch (err) {
      errorAlert({ title: errorTitle, exception: err });
    } finally {
      setLoading(false);
    }
  };

  const handleReabrirSubtramite = async () => {
    setLoading(true);
    try {
      const tablesMap = {
        reserva: 'reservaprioridad',
        presupuesto: 'presupuestos',
      }
      const result = await updateSubtramiteEstado({
        tipo: tablesMap[subtramiteKey] || subtramiteKey,
        id: data.id,
        estado: 'en-proceso',
      });

      await navigate(pathname, {
        state: {
          ...state,
          tramite: mapResult(result, state.categoria, subtramiteKey),
        },
        replace: true,
        flushSync: true,
      });

      successAlert({ message: 'Subtrámite reabierto con éxito' });
    } catch (err) {
      errorAlert({ title: 'Error al reabrir subtrámite', exception: err });
    } finally {
      setLoading(false);
    }
  };

  return {
    data,
    setData,
    loading,
    init,
    isUpdate,
    setIsUpdate,
    handleChange,
    handleSubmit,
    btnGuardarVisible,
    handleFinalizarSubtramite,
    handleReabrirSubtramite,
  };
};
